import React, { useEffect, useState } from "react";
import {
  Stack,
  Typography,
  Divider,
  TextField,
  MenuItem,
  Button,
  ButtonGroup,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Tooltip,
  Box,
  Alert,
  Chip,
  ListSubheader,
  Tabs,
  Tab,
} from "@mui/material";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import DeleteIcon from "@mui/icons-material/Delete";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import TextIncreaseIcon from "@mui/icons-material/TextIncrease";
import TextDecreaseIcon from "@mui/icons-material/TextDecrease";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import VerticalAlignCenterIcon from "@mui/icons-material/VerticalAlignCenter";
import VerticalAlignBottomIcon from "@mui/icons-material/VerticalAlignBottom";
import {
  LABEL_PRESETS,
  LABEL_FIELDS,
  labelPitchMm,
  appendField,
  FIELD_SEPARATOR,
  ZPL_FONTS,
  fontByKey,
  legalFontSize,
} from "../labelSchema";
import { SYSTEM_FONTS, systemFontByKey } from "../render/textRaster";
import {
  qrModuleCount,
  qrPayloadTooLong,
  alignedPosition,
  contentBox,
  textOverflowsBlock,
  widthToFitTextMm,
} from "../render/labelGeometry";

/**
 * Largest whole magnification whose symbol still fits inside the padded label.
 * Payloads grow as fields are added, so this is what tells the user how much room
 * is actually left rather than making them discover it on a printed sticker.
 */
const maxMagnificationThatFits = (el, label) => {
  const b = contentBox(label);
  const usableMm = Math.min(b.right - b.left, b.bottom - b.top);
  const modules = qrModuleCount(el.text, el.ecLevel);
  return Math.max(1, Math.floor((usableMm * label.dpmm) / modules));
};

const num = (label, value, onChange, props = {}) => (
  <TextField
    size="small"
    type="number"
    label={label}
    value={value}
    fullWidth
    inputProps={{ min: props.min, max: props.max, step: props.step || 1 }}
    helperText={props.helperText}
    onChange={(e) => {
      if (e.target.value === "") return;
      let v = Number(e.target.value);
      if (Number.isNaN(v)) return;
      if (props.min != null) v = Math.max(props.min, v);
      if (props.max != null) v = Math.min(props.max, v);
      onChange(v);
    }}
  />
);

const ROTATIONS = [
  { value: 0, label: "0°" },
  { value: 90, label: "90°" },
  { value: 180, label: "180°" },
  { value: 270, label: "270°" },
];

/* ------------------------------------------------------------------ */
/* Shared pieces of the element tab                                    */
/* ------------------------------------------------------------------ */

function PositionAndAlign({ el, label, patchEl }) {
  return (
    <>
      <Stack direction="row" spacing={1}>
        {num("X (mm)", el.xMm, (v) => patchEl({ xMm: v }), { min: 0, step: 0.5 })}
        {num("Y (mm)", el.yMm, (v) => patchEl({ yMm: v }), { min: 0, step: 0.5 })}
      </Stack>

      {/* Dragging by eye is never exact; these compute the position from the
          element's real printed footprint and the padded content box. */}
      <Typography variant="caption" color="text.secondary">
        Align in label
      </Typography>
      <Stack direction="row" spacing={1}>
        <ButtonGroup size="small" fullWidth>
          {[
            ["left", FormatAlignLeftIcon, "Left"],
            ["centerX", FormatAlignCenterIcon, "Centre"],
            ["right", FormatAlignRightIcon, "Right"],
          ].map(([how, Icon, tip]) => (
            <Tooltip key={how} title={tip}>
              <Button onClick={() => patchEl(alignedPosition(el, label, how))}>
                <Icon fontSize="small" />
              </Button>
            </Tooltip>
          ))}
        </ButtonGroup>
        <ButtonGroup size="small" fullWidth>
          {[
            ["top", VerticalAlignTopIcon, "Top"],
            ["centerY", VerticalAlignCenterIcon, "Middle"],
            ["bottom", VerticalAlignBottomIcon, "Bottom"],
          ].map(([how, Icon, tip]) => (
            <Tooltip key={how} title={tip}>
              <Button onClick={() => patchEl(alignedPosition(el, label, how))}>
                <Icon fontSize="small" />
              </Button>
            </Tooltip>
          ))}
        </ButtonGroup>
      </Stack>

      <TextField
        select
        size="small"
        label="Rotation"
        value={el.rotation}
        fullWidth
        onChange={(e) => patchEl({ rotation: Number(e.target.value) })}
      >
        {ROTATIONS.map((r) => (
          <MenuItem key={r.value} value={r.value}>
            {r.label}
          </MenuItem>
        ))}
      </TextField>
    </>
  );
}

function FieldChips({ el, patchEl }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        {el.type === "qr" ? `Insert a field — joined with “${FIELD_SEPARATOR}”` : "Insert a field"}
      </Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {LABEL_FIELDS.map((f) => (
          <Chip
            key={f.key}
            size="small"
            variant="outlined"
            label={f.label}
            // A QR payload is parsed field-by-field on the scanning side, so fields
            // are delimited. Label text is read by a human, so it is not.
            onClick={() =>
              patchEl({
                text: appendField(el.text, f.key, el.type === "qr" ? FIELD_SEPARATOR : " "),
              })
            }
          />
        ))}
      </Stack>
    </Box>
  );
}

function QrProperties({ el, label, patchEl }) {
  const modules = qrModuleCount(el.text, el.ecLevel);
  const sizeMm = ((modules * el.magnification) / label.dpmm).toFixed(1);
  const stepMm = (modules / label.dpmm).toFixed(1);
  const fitMax = maxMagnificationThatFits(el, label);

  return (
    <Stack spacing={2}>
      <TextField
        size="small"
        label="QR data"
        value={el.text}
        multiline
        minRows={2}
        fullWidth
        onChange={(e) => patchEl({ text: e.target.value })}
      />
      <FieldChips el={el} patchEl={patchEl} />

      <Stack direction="row" spacing={1}>
        {num("Magnification", el.magnification, (v) => patchEl({ magnification: v }), {
          min: 1,
          max: 30,
          helperText:
            el.magnification > fitMax
              ? `${modules} modules → ${sizeMm} mm · too big, ${fitMax} fits`
              : `${modules} modules → ${sizeMm} mm · steps of ${stepMm} mm`,
        })}
        <TextField
          select
          size="small"
          label="Error correction"
          value={el.ecLevel}
          fullWidth
          onChange={(e) => patchEl({ ecLevel: e.target.value })}
        >
          <MenuItem value="L">L – 7%</MenuItem>
          <MenuItem value="M">M – 15%</MenuItem>
          <MenuItem value="Q">Q – 25%</MenuItem>
          <MenuItem value="H">H – 30%</MenuItem>
        </TextField>
      </Stack>

      {qrPayloadTooLong(el.text, el.ecLevel) && (
        <Alert severity="error" sx={{ fontSize: 12 }}>
          This payload is too long to encode. Shorten it or lower the error correction level.
        </Alert>
      )}

      <Divider />
      <PositionAndAlign el={el} label={label} patchEl={patchEl} />
    </Stack>
  );
}

function TextProperties({ el, label, patchEl }) {
  const isSystem = el.fontSource === "system";
  const singleLine = (el.maxLines || 1) === 1;

  const setSize = (v) =>
    patchEl(isSystem ? { fontH: Math.max(6, v) } : legalFontSize(el.fontKey || "0", v));

  return (
    <Stack spacing={2}>
      <TextField
        size="small"
        label="Text"
        value={el.text}
        multiline
        minRows={2}
        fullWidth
        onChange={(e) => patchEl({ text: e.target.value })}
      />
      <FieldChips el={el} patchEl={patchEl} />

      {/* Laid out like a word-processor font toolbar. */}
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          select
          size="small"
          label="Font"
          value={isSystem ? `sys:${el.fontFamily}` : `zpl:${el.fontKey || "0"}`}
          sx={{ flex: 1 }}
          onChange={(e) => {
            const [kind, key] = e.target.value.split(":");
            if (kind === "sys") {
              patchEl({ fontSource: "system", fontFamily: key, fontW: 0 });
            } else {
              patchEl({
                fontSource: "printer",
                fontKey: key,
                italic: false, // no resident font has one
                ...legalFontSize(key, el.fontH),
              });
            }
          }}
        >
          <ListSubheader>Printer fonts</ListSubheader>
          {ZPL_FONTS.map((f) => (
            <MenuItem key={f.key} value={`zpl:${f.key}`}>
              {f.label}
            </MenuItem>
          ))}
          <ListSubheader>Standard</ListSubheader>
          {SYSTEM_FONTS.map((f) => (
            <MenuItem key={f.key} value={`sys:${f.key}`}>
              {f.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          type="number"
          label="Size"
          value={el.fontH}
          sx={{ width: 80 }}
          inputProps={{ min: 6, max: 300 }}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (!Number.isNaN(v)) setSize(v);
          }}
        />
        <ButtonGroup size="small" sx={{ mt: 0.25 }}>
          <Tooltip title="Larger">
            <Button onClick={() => setSize(el.fontH + 2)}>
              <TextIncreaseIcon fontSize="small" />
            </Button>
          </Tooltip>
          <Tooltip title="Smaller">
            <Button onClick={() => setSize(Math.max(6, el.fontH - 2))}>
              <TextDecreaseIcon fontSize="small" />
            </Button>
          </Tooltip>
        </ButtonGroup>
      </Stack>

      <Typography variant="caption" color="text.secondary">
        {isSystem
          ? `${systemFontByKey(el.fontFamily).label} is drawn here and sent as a bitmap — any size, and italic works.`
          : fontByKey(el.fontKey || "0").scalable
          ? "Scalable, proportional — any height"
          : `Bitmap ${fontByKey(el.fontKey || "0").baseH}×${
              fontByKey(el.fontKey || "0").baseW
            } dots — height snaps to whole multiples`}
      </Typography>

      <Stack direction="row" spacing={1}>
        <ButtonGroup size="small">
          <Tooltip title="Bold">
            <Button
              variant={el.bold ? "contained" : "outlined"}
              onClick={() => patchEl({ bold: !el.bold })}
            >
              <FormatBoldIcon fontSize="small" />
            </Button>
          </Tooltip>
          <Tooltip
            title={
              isSystem
                ? "Italic"
                : "No printer font has an italic face — choose a Standard font such as Arial"
            }
          >
            <span>
              <Button
                variant={el.italic ? "contained" : "outlined"}
                disabled={!isSystem}
                onClick={() => patchEl({ italic: !el.italic })}
              >
                <FormatItalicIcon fontSize="small" />
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={singleLine ? "Underline" : "Single-line text only"}>
            <span>
              <Button
                variant={el.underline ? "contained" : "outlined"}
                disabled={!singleLine}
                onClick={() => patchEl({ underline: !el.underline })}
              >
                <FormatUnderlinedIcon fontSize="small" />
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={singleLine ? "Strike-through" : "Single-line text only"}>
            <span>
              <Button
                variant={el.strikethrough ? "contained" : "outlined"}
                disabled={!singleLine}
                onClick={() => patchEl({ strikethrough: !el.strikethrough })}
              >
                <StrikethroughSIcon fontSize="small" />
              </Button>
            </span>
          </Tooltip>
        </ButtonGroup>

        <ButtonGroup size="small">
          {[
            ["L", FormatAlignLeftIcon, "Left"],
            ["C", FormatAlignCenterIcon, "Centre"],
            ["R", FormatAlignRightIcon, "Right"],
          ].map(([val, Icon, tip]) => (
            <Tooltip key={val} title={tip}>
              <Button
                variant={el.align === val ? "contained" : "outlined"}
                onClick={() => patchEl({ align: val })}
              >
                <Icon fontSize="small" />
              </Button>
            </Tooltip>
          ))}
        </ButtonGroup>
      </Stack>

      {textOverflowsBlock(el, label.dpmm) && (
        <Alert
          severity="error"
          sx={{ fontSize: 12 }}
          action={
            <Button
              size="small"
              color="inherit"
              onClick={() => patchEl({ widthMm: widthToFitTextMm(el, label.dpmm) })}
            >
              Fit box
            </Button>
          }
        >
          Too wide for the box — the overflow prints on top of the last line.
        </Alert>
      )}

      <Stack direction="row" spacing={1}>
        {num("Box width (mm)", el.widthMm, (v) => patchEl({ widthMm: v }), {
          min: 2,
          max: 200,
          step: 0.5,
        })}
        {num("Max lines", el.maxLines, (v) => patchEl({ maxLines: v }), { min: 1, max: 10 })}
      </Stack>

      <Divider />
      <PositionAndAlign el={el} label={label} patchEl={patchEl} />
    </Stack>
  );
}

/* ------------------------------------------------------------------ */
/* Panel                                                               */
/* ------------------------------------------------------------------ */

export default function LabelPropertiesPanel({
  label,
  patch,
  selectedId,
  onSelect,
  onAddQr,
  onAddText,
  onDeleteElement,
  onPatchElement,
}) {
  const el = label.elements.find((e) => e.id === selectedId);
  const patchEl = (p) => onPatchElement(selectedId, p);
  const [tab, setTab] = useState(0);

  // Selecting something on the canvas is a request to edit it, so bring its
  // properties forward instead of leaving the user on a tab they are not using.
  useEffect(() => {
    if (selectedId) setTab(0);
  }, [selectedId]);

  const presetIdx = LABEL_PRESETS.findIndex(
    (p) => p.widthMm === label.widthMm && p.heightMm === label.heightMm
  );

  return (
    <Stack sx={{ height: "100%", minHeight: 0 }}>
      {/* Always reachable: adding and choosing an element drives everything else. */}
      <Box sx={{ p: 1.5, pb: 1 }}>
        <ButtonGroup size="small" fullWidth sx={{ mb: 1 }}>
          <Button startIcon={<QrCode2Icon />} onClick={onAddQr}>
            QR
          </Button>
          <Button startIcon={<TextFieldsIcon />} onClick={onAddText}>
            Text
          </Button>
        </ButtonGroup>

        <List
          dense
          disablePadding
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            maxHeight: 132,
            overflow: "auto",
          }}
        >
          {label.elements.length === 0 && (
            <Box p={1.5}>
              <Typography variant="caption" color="text.secondary">
                No elements yet — add a QR or some text.
              </Typography>
            </Box>
          )}
          {label.elements.map((e) => (
            <ListItem
              key={e.id}
              disablePadding
              secondaryAction={
                <Tooltip title="Delete element">
                  <IconButton
                    edge="end"
                    size="small"
                    aria-label={`Delete ${e.type === "qr" ? "QR code" : "text"}`}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      onDeleteElement(e.id);
                    }}
                  >
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemButton selected={e.id === selectedId} onClick={() => onSelect(e.id)}>
                {e.type === "qr" ? (
                  <QrCode2Icon fontSize="small" />
                ) : (
                  <TextFieldsIcon fontSize="small" />
                )}
                <ListItemText
                  sx={{ ml: 1, mr: 2 }}
                  primary={e.type === "qr" ? "QR code" : "Text"}
                  secondary={String(e.text).slice(0, 28)}
                  primaryTypographyProps={{ variant: "body2" }}
                  secondaryTypographyProps={{ variant: "caption", noWrap: true }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        variant="fullWidth"
        sx={{ borderTop: 1, borderBottom: 1, borderColor: "divider", minHeight: 40 }}
      >
        <Tab
          label={el ? (el.type === "qr" ? "QR code" : "Text") : "Element"}
          sx={{ minHeight: 40 }}
        />
        <Tab label="Label" sx={{ minHeight: 40 }} />
        <Tab label="Page" sx={{ minHeight: 40 }} />
      </Tabs>

      {/* Only this scrolls, so the label stays in view while settings change. */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 1.5 }}>
        {tab === 0 &&
          (el ? (
            el.type === "qr" ? (
              <QrProperties el={el} label={label} patchEl={patchEl} />
            ) : (
              <TextProperties el={el} label={label} patchEl={patchEl} />
            )
          ) : (
            <Box sx={{ textAlign: "center", color: "text.secondary", py: 4 }}>
              <Typography variant="body2">Nothing selected.</Typography>
              <Typography variant="caption">
                Click an element on the label, or pick one from the list above.
              </Typography>
            </Box>
          ))}

        {tab === 1 && (
          <Stack spacing={2}>
            <TextField
              select
              size="small"
              label="Preset"
              value={presetIdx}
              fullWidth
              onChange={(e) => {
                const p = LABEL_PRESETS[Number(e.target.value)];
                if (p) {
                  patch({
                    widthMm: p.widthMm,
                    heightMm: p.heightMm,
                    columns: p.columns,
                    gapXMm: p.gapXMm,
                  });
                }
              }}
            >
              <MenuItem value={-1} disabled>
                Custom
              </MenuItem>
              {LABEL_PRESETS.map((p, i) => (
                <MenuItem key={p.label} value={i}>
                  {p.label}
                </MenuItem>
              ))}
            </TextField>
            <Stack direction="row" spacing={1}>
              {num("Width (mm)", label.widthMm, (v) => patch({ widthMm: v }), {
                min: 5,
                max: 200,
                step: 0.5,
              })}
              {num("Height (mm)", label.heightMm, (v) => patch({ heightMm: v }), {
                min: 5,
                max: 400,
                step: 0.5,
              })}
            </Stack>
            <Stack direction="row" spacing={1}>
              {num("Padding (mm)", label.paddingMm, (v) => patch({ paddingMm: v }), {
                min: 0,
                max: 10,
                step: 0.5,
                helperText: "Safe inset",
              })}
              {num("Resolution", label.dpmm, (v) => patch({ dpmm: v }), {
                min: 8,
                max: 12,
                step: 4,
                helperText: "8 = 203 dpi",
              })}
            </Stack>
          </Stack>
        )}

        {tab === 2 && (
          <Stack spacing={2}>
            <Typography variant="subtitle2">Labels per page</Typography>
            <Stack direction="row" spacing={1}>
              {num("Columns", label.columns, (v) => patch({ columns: v }), { min: 1, max: 10 })}
              {num("Rows", label.rows, (v) => patch({ rows: v }), { min: 1, max: 20 })}
            </Stack>
            <Stack direction="row" spacing={1}>
              {num("Gap X (mm)", label.gapXMm, (v) => patch({ gapXMm: v }), {
                min: 0,
                max: 50,
                step: 0.5,
              })}
              {num("Gap Y (mm)", label.gapYMm, (v) => patch({ gapYMm: v }), {
                min: 0,
                max: 50,
                step: 0.5,
              })}
            </Stack>
            <Alert severity="info" icon={false} sx={{ fontSize: 12, py: 0.5 }}>
              <strong>Pitch: {labelPitchMm(label).toFixed(2)} mm</strong> — sticker edge to the same
              edge of the next one. Measure across 3 stickers and divide by 2; if it disagrees with
              the roll, every label after the first drifts by the difference.
            </Alert>

            <Divider />
            <Typography variant="subtitle2">Media offset</Typography>
            <Typography variant="caption" color="text.secondary">
              Where the media sits relative to the print head's origin. If every label is off by the
              same amount in the same direction, adjust this.
            </Typography>
            <Stack direction="row" spacing={1}>
              {num("Offset X (mm)", label.offsetXMm, (v) => patch({ offsetXMm: v }), {
                min: 0,
                max: 30,
                step: 0.1,
              })}
              {num("Offset Y (mm)", label.offsetYMm, (v) => patch({ offsetYMm: v }), {
                min: 0,
                max: 30,
                step: 0.1,
              })}
            </Stack>
          </Stack>
        )}
      </Box>
    </Stack>
  );
}
